import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SideBar from "../Component/SideBar";
import SchoolIcon from "@mui/icons-material/School";
import HistoryIcon from "@mui/icons-material/History";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import { fetchDashboardData, fetchTestAttempts } from "../Api/dashboardApi";

export default function Dashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [summary, setSummary] = useState({
    totalExam: 0,
    testAttempted: 0,
    testAttemptedLastWeek: 0,
    lastScore: 0,
  });

  const [recentExams, setRecentExams] = useState([]);

  const summaryCards = [
    {
      label: "Total Exams",
      value: summary.totalExam,
      icon: <SchoolIcon fontSize="large" color="primary" />,
    },
    {
      label: "Tests Attempted",
      value: summary.testAttempted,
      icon: <AssessmentIcon fontSize="large" color="warning" />,
    },
    {
      label: "Attempted Last Week",
      value: summary.testAttemptedLastWeek,
      icon: <HistoryIcon fontSize="large" color="success" />,
    },
    {
      label: "Last Score",
      value: `${summary.lastScore}%`,
      icon: <EmojiEventsIcon fontSize="large" color="error" />,
    },
  ];

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const data = await fetchDashboardData();
        setSummary({
          totalExam: data.totalExam || 0,
          testAttempted: data.testAttempted || 0,
          testAttemptedLastWeek: data.testAttemptedLastWeek || 0,
          lastScore: data.lastScore || 0,
        });
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
      }
    };

    const loadTestAttempts = async () => {
      try {
        const response = await fetchTestAttempts();
        const attempts = (response.data || [])
          .sort(
            (a, b) =>
              new Date(b.submittedAt).getTime() -
              new Date(a.submittedAt).getTime()
          )
          .slice(0, 5);

        const nameCount = {};
        attempts.forEach((attempt) => {
          const name = attempt.generalInformation?.name || "Unknown Test";
          nameCount[name] = (nameCount[name] || 0) + 1;
        });

        const nameSeen = {};
        const processed = attempts.map((attempt) => {
          const name = attempt.generalInformation?.name || "Unknown Test";
          nameSeen[name] = (nameSeen[name] || 0) + 1;
          const isDuplicate = nameCount[name] > 1;

          return {
            id: attempt.id,
            name: name,
            score: attempt.testResult?.percentage ?? 0,
            submittedAt: attempt.submittedAt,
            displayName: isDuplicate ? `${name} #${nameSeen[name]}` : name,
            originalName: name,
            formattedDate: new Date(attempt.submittedAt).toLocaleString(),
          };
        });

        setRecentExams(processed);
      } catch (error) {
        console.error("Failed to load test attempts:", error);
      }
    };

    loadDashboard();
    loadTestAttempts();
  }, []);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper
          sx={{
            p: 2,
            maxWidth: 250,
            border: "1px solid #ccc",
            boxShadow: 3,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            <strong>Test Name:</strong> {data.originalName}
          </Typography>
          <Typography variant="body2">
            <strong>Submitted:</strong> {data.formattedDate}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Score:</strong> {data.score}%
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  const formatXAxisLabel = (value) => {
    return value.length > 15 ? `${value.substring(0, 12)}...` : value;
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f4f6f8" }}>
      <SideBar />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          mt: { xs: "56px", sm: "64px" },
          width: "100%",
        }}
      >
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          {summaryCards.map((card, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 3,
                  bgcolor: "#fff",
                  height: "100%",
                }}
              >
                {card.icon}
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  {card.label}
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {card.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper sx={{ p: 3, borderRadius: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Score Analysis (Last 5 Tests)
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {recentExams.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={recentExams} margin={{ bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="displayName"
                  tickFormatter={formatXAxisLabel}
                  interval={0}
                  angle={0}
                  textAnchor="middle"
                  height={60}
                />
                <YAxis domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="score"
                  fill="#1976d2"
                  stroke="#1565c0"
                  strokeWidth={1}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 300,
                color: "text.secondary",
              }}
            >
              <Typography variant="body1">No test attempts found</Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
